import { forwardRef, type TextareaHTMLAttributes } from 'react';

export type TextareaSize = 'sm' | 'md';

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * `md` (default) is the standard comfortable field (6rem min-height); `sm` is
   * a compact variant (smaller min-height + text-body-sm) for dense rows where
   * the comfortable floor would inflate the layout.
   */
  size?: TextareaSize;
  invalid?: boolean;
  className?: string;
}

const BASE =
  'w-full rounded-control border bg-surface text-primary border-border placeholder:text-muted ' +
  'px-[var(--field-pad)] resize-y text-body-sm ' +
  'focus-visible:outline-none focus-visible:border-ring-focus ' +
  'focus-visible:ring-[3px] focus-visible:ring-ring-focus/20 ' +
  '[&:-webkit-autofill]:bg-surface [&:-webkit-autofill]:text-primary ' +
  'disabled:opacity-disabled disabled:cursor-not-allowed';

const SIZE: Record<TextareaSize, string> = {
  sm: 'min-h-[1.875rem] py-1.5', // 30px floor, compact
  md: 'min-h-[6rem] py-2', // comfortable default
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { size = 'md', invalid = false, className, ...rest },
  ref,
) {
  const classes = [
    BASE,
    SIZE[size],
    invalid ? 'border-danger focus-visible:ring-danger' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return <textarea ref={ref} aria-invalid={invalid || undefined} className={classes} {...rest} />;
});
